import React, { useState, useEffect } from 'react';
import { api } from './api/api';

const parseIdArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  if (typeof value === 'string') { try { return JSON.parse(value); } catch { return [value]; } }
  return [value];
};

const normalizeType = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const findServiceByTypeAndWeight = (serviceType, weight, servicesList) => {
  if (!serviceType) return null;
  const normalizedType = normalizeType(serviceType);
  const matchingType = servicesList.filter((s) => normalizeType(s.type) === normalizedType);
  if (matchingType.length === 0) return null;

  const sorted = matchingType
    .map((s) => ({
      ...s,
      rangeFrom: Number(s.range_from ?? s.rangeFrom ?? 0),
      rangeTo: Number(s.range_to ?? s.rangeTo ?? Infinity)
    }))
    .sort((a, b) => a.rangeFrom - b.rangeFrom);

  const byWeight = sorted.find((s) => {
    const from = Number.isFinite(s.rangeFrom) ? s.rangeFrom : 0;
    const to = Number.isFinite(s.rangeTo) ? s.rangeTo : Infinity;
    return weight >= from && weight <= to;
  });

  return byWeight || sorted[0] || null;
};

// ─── Templates ───
const emptyHouseBol = {
  bolReference: '',
  bolTypeCode: '740',
  numberOfPackages: '0',
  grossMass: '0',
  shippingMarks: 'A',
  goodsDescription: '',
  services: [],
  packingItems: [],
  buildUpItems: []
};

const emptyMaster = {
  tempId: null,
  bolReference: '',
  bolTypeCode: '740',
  customerName: '',
  numberOfPackages: '',
  grossMass: '',
  shippingMarks: '',
  goodsDescription: '',
  cargoTypes: [],
  services: [],
  packingItems: [],
  buildUpItems: [],
  homes: []
};

const OperatorApplicationForm = () => {
  // ─── الهيدر الثابت (مشترك لكل الماسترات) ───
  const [header, setHeader] = useState({
    ownerOperator: '',
    aircraftRegistration: '',
    declaredTotalAwbs: '',
    customsOfficeCode: '',
    voyageFlightNo: '',
    dateOfDeparture: '',
    etd: '',
    portOfLoading: '',
    portOfDischarge: ''
  });

  // ─── الماستر قيد الإدخال حالياً ───
  const [currentMaster, setCurrentMaster] = useState(null);

  // ─── الماسترات المحفوظة ───
  const [savedMasters, setSavedMasters] = useState([]);

  // ─── البيانات الأخرى ───
  const [submittedBols, setSubmittedBols] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [servicesList, setServicesList] = useState([]);
  const [packingItems, setPackingItems] = useState([]);
  const [buildUpItems, setBuildUpItems] = useState([]);
  const [cargoTypesList, setCargoTypesList] = useState([]);

  // ─── Effects ───
  useEffect(() => {
    fetchLists();
    loadSubmittedBols();

    const handleFocus = () => {
      fetchLists();
      loadSubmittedBols();
    };

    window.addEventListener('focus', handleFocus);
    const interval = setInterval(loadSubmittedBols, 20000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, []);

  const fetchLists = async () => {
    try {
      const [servicesRes, packingRes, buildUpRes, cargoTypesRes] = await Promise.all([
        api.get('/services'),
        api.get('/packing'),
        api.get('/build-up'),
        api.get('/cargo-types')
      ]);
      setServicesList(Array.isArray(servicesRes) ? servicesRes : []);
      setPackingItems(Array.isArray(packingRes) ? packingRes : []);
      setBuildUpItems(Array.isArray(buildUpRes) ? buildUpRes : []);
      setCargoTypesList(Array.isArray(cargoTypesRes) ? cargoTypesRes : []);
    } catch (err) { console.error('Error fetching lists:', err); }
  };

  const loadSubmittedBols = async () => {
    try {
      const response = await api.get('/bols/pending/returned');
      const arr = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.rows)
            ? response.rows
            : [];
      setSubmittedBols(arr.filter(b => b.returned_for_review == 1));
    } catch (err) {
      console.error('Error fetching pending BOLs:', err);
      setSubmittedBols([]);
    }
  };

  // ─── تغيير الهيدر ───
  const handleHeaderChange = (field, value) => {
    setHeader(prev => ({ ...prev, [field]: value }));
  };

  // ─── إضافة ماستر جديد (نفس زر Add House بالضبط) ───
  const addNewMaster = () => {
    // إذا كان هناك ماستر قيد الإدخال، نحفظه أولاً
    if (currentMaster) {
      const confirmSave = window.confirm('You have a master in progress. Save it first?');
      if (confirmSave) {
        saveCurrentMaster();
      } else {
        return; // لا تفعل شيئاً إذا رفض
      }
    }

    setCurrentMaster({
      ...emptyMaster,
      tempId: `master_${Date.now()}`
    });
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  // ─── حفظ الماستر الحالي في القائمة ───
  const saveCurrentMaster = () => {
    if (!currentMaster) return;
    if (!currentMaster.bolReference) return alert('Please enter AWB Reference');
    if (currentMaster.cargoTypes.length === 0) return alert('Please select at least one Cargo Type');
    if (currentMaster.services.length === 0) return alert('Please select at least one Service');
    // if (currentMaster.packingItems.length === 0) return alert('Please select at least one Packing Item');
    // if (currentMaster.buildUpItems.length === 0) return alert('Please select at least one Build-up Item');
    if (!currentMaster.grossMass || !currentMaster.numberOfPackages) return alert('Please enter Weight and Packages');

    setSavedMasters(prev => [...prev, currentMaster]);
    setCurrentMaster(null);
    alert('Master AWB saved ✅');
  };

  // ─── إلغاء الماستر الحالي ───
  const cancelCurrentMaster = () => {
    setCurrentMaster(null);
  };

  // ─── حذف ماستر محفوظ ───
  const removeSavedMaster = (tempId) => {
    setSavedMasters(prev => prev.filter(m => m.tempId !== tempId));
  };

  // ─── تعديل ماستر محفوظ ───
  const editSavedMaster = (master) => {
    if (currentMaster) {
      alert('Please save or cancel the current master first');
      return;
    }
    setSavedMasters(prev => prev.filter(m => m.tempId !== master.tempId));
    setCurrentMaster(master);
  };

  // ─── إضافة هاوس للماستر الحالي ───
  const addHouseToCurrent = () => {
    if (!currentMaster) return;
    const newHouse = { ...emptyHouseBol, tempId: `house_${Date.now()}_${Math.random()}` };
    setCurrentMaster(prev => {
      const newHomes = [...prev.homes, newHouse];
      const totalPkgs = newHomes.reduce((sum, h) => sum + (Number(h.numberOfPackages) || 0), 0);
      const totalWeight = newHomes.reduce((sum, h) => sum + (Number(h.grossMass) || 0), 0);
      return {
        ...prev,
        homes: newHomes,
        numberOfPackages: totalPkgs.toString(),
        grossMass: totalWeight.toString(),
        bolTypeCode: '741'
      };
    });
  };

  // ─── حذف هاوس من الماستر الحالي ───
  const removeHouseFromCurrent = (tempId) => {
    if (!currentMaster) return;
    setCurrentMaster(prev => {
      const newHomes = prev.homes.filter(h => h.tempId !== tempId);
      const totalPkgs = newHomes.reduce((sum, h) => sum + (Number(h.numberOfPackages) || 0), 0);
      const totalWeight = newHomes.reduce((sum, h) => sum + (Number(h.grossMass) || 0), 0);
      return {
        ...prev,
        homes: newHomes,
        numberOfPackages: totalPkgs.toString(),
        grossMass: totalWeight.toString(),
        bolTypeCode: newHomes.length > 0 ? '741' : '740'
      };
    });
  };

  // ─── تعديل حقل في الماستر الحالي ───
  const updateCurrentMaster = (field, value) => {
    if (!currentMaster) return;
    setCurrentMaster(prev => ({ ...prev, [field]: value }));
  };

  // ─── تعديل حقل في هاوس ───
  const updateHouseField = (houseIdx, field, value) => {
    if (!currentMaster) return;
    const newHomes = [...currentMaster.homes];
    newHomes[houseIdx] = { ...newHomes[houseIdx], [field]: value };

    const totalPkgs = newHomes.reduce((sum, h) => sum + (Number(h.numberOfPackages) || 0), 0);
    const totalWeight = newHomes.reduce((sum, h) => sum + (Number(h.grossMass) || 0), 0);

    setCurrentMaster(prev => ({
      ...prev,
      homes: newHomes,
      numberOfPackages: totalPkgs.toString(),
      grossMass: totalWeight.toString(),
      bolTypeCode: newHomes.length > 0 ? '741' : '740'
    }));
  };

  // ─── Services Logic ───
  const uniqueServiceTypes = [...new Set(servicesList.map(s => s.type).filter(Boolean))];

  const handleAutoServiceSelect = (type) => {
    if (!currentMaster) return;
    const weight = Number(currentMaster.grossMass) || 0;
    if (weight <= 0) {
      alert('Please enter Gross Mass (Weight) first');
      return;
    }
    const matched = findServiceByTypeAndWeight(type, weight, servicesList);
    if (matched) {
      if (!currentMaster.services.some(s => s.type === matched.type)) {
        setCurrentMaster(prev => ({
          ...prev,
          services: [...prev.services, { ...matched, showPacking: false, selectedPackingId: null }]
        }));
      } else {
        alert('This service type is already selected.');
      }
    }
  };

  const handleHouseAutoServiceSelect = (houseIdx, type) => {
    if (!currentMaster) return;
    const house = currentMaster.homes[houseIdx];
    const weight = Number(house.grossMass) || 0;
    if (weight <= 0) return alert('Please enter Weight for this House AWB first.');

    const matched = findServiceByTypeAndWeight(type, weight, servicesList);
    if (matched) {
      const newHomes = [...currentMaster.homes];
      if (!newHomes[houseIdx].services.some(s => s.type === matched.type)) {
        newHomes[houseIdx].services = [...newHomes[houseIdx].services, { ...matched, showPacking: false, selectedPackingId: null }];
        setCurrentMaster(prev => ({ ...prev, homes: newHomes }));
      } else {
        alert('This service type is already selected for this House AWB.');
      }
    }
  };

  // ─── Submit All ───
  const submitAllMasters = async () => {
    if (!header.ownerOperator) return alert('Please enter Owner/Operator');
    if (!header.aircraftRegistration) return alert('Please enter Aircraft Registration');
    if (!header.portOfLoading) return alert('Please enter Point of Loading');
    if (!header.portOfDischarge) return alert('Please enter Point of Discharge');

    // إذا كان هناك ماستر قيد الإدخال، نحفظه أولاً
    let allMasters = [...savedMasters];
    if (currentMaster) {
      const confirmSave = window.confirm('You have a master in progress. Save it before submitting?');
      if (confirmSave) {
        const isValid = validateCurrentMaster();
        if (!isValid) return;
        allMasters.push(currentMaster);
      }
    }

    if (allMasters.length === 0) return alert('No masters to submit');

    try {
      for (const master of allMasters) {
        const payload = {
          owner_operator: header.ownerOperator,
          aircraft_registration: header.aircraftRegistration,
          declared_total_awbs: header.declaredTotalAwbs,
          customs_office_code: header.customsOfficeCode,
          voyage_flight_no: header.voyageFlightNo,
          date_of_departure: header.dateOfDeparture || null,
          etd: header.etd || null,
          port_of_loading: header.portOfLoading,
          port_of_discharge: header.portOfDischarge,
          bol_reference: master.bolReference,
          bol_nature: '23',
          bol_type_code: master.bolTypeCode,
          customer_name: master.customerName,
          number_of_packages: master.numberOfPackages,
          gross_mass: master.grossMass,
          shipping_marks: master.shippingMarks,
          goods_description: master.goodsDescription,
          status: 'pending_services',
          cargo_types: master.cargoTypes,
          service_ids: master.services.map(s => s.id),
          packing_item_ids: master.packingItems.map(p => p.id),
          build_up_item_ids: master.buildUpItems.map(b => b.id),
          homes: master.homes.map(h => ({
            bol_reference: h.bolReference,
            bol_nature: '23',
            bol_type_code: h.bolTypeCode,
            number_of_packages: h.numberOfPackages,
            gross_mass: h.grossMass,
            shipping_marks: h.shippingMarks,
            goods_description: h.goodsDescription,
            service_ids: h.services.map(s => s.id),
            packing_item_ids: h.packingItems.map(p => p.id),
            build_up_item_ids: h.buildUpItems.map(b => b.id)
          }))
        };

        await api.post('/bols/operator-submission', payload);
      }

      alert('All master AWBs submitted successfully ✅');
      setSavedMasters([]);
      setCurrentMaster(null);
      setIsSubmitted(true);
      await loadSubmittedBols();
      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (err) {
      console.error('Error submitting masters:', err);
      alert('Error submitting masters');
    }
  };

  const validateCurrentMaster = () => {
    if (!currentMaster) return false;
    if (!currentMaster.bolReference) { alert('Please enter AWB Reference'); return false; }
    if (currentMaster.cargoTypes.length === 0) { alert('Please select Cargo Type'); return false; }
    if (currentMaster.services.length === 0) { alert('Please select Service'); return false; }
    // if (currentMaster.packingItems.length === 0) { alert('Please select Packing'); return false; }
    // if (currentMaster.buildUpItems.length === 0) { alert('Please select Build-up'); return false; }
    if (!currentMaster.grossMass || !currentMaster.numberOfPackages) { alert('Please enter Weight and Packages'); return false; }
    return true;
  };

  // ─── Edit Returned BOL ───
  const editReturnedBol = (bol) => {
    const normalizedHomes = (bol.homes || []).map(h => ({
      tempId: h.tempId || `house_${Math.random()}`,
      bolReference: h.bol_reference || h.bolReference || '',
      bolTypeCode: h.bol_type_code || h.bolTypeCode || '740',
      numberOfPackages: h.number_of_packages || h.numberOfPackages || '',
      grossMass: h.gross_mass || h.grossMass || '',
      shippingMarks: h.shipping_marks || h.shippingMarks || 'A',
      goodsDescription: h.goods_description || h.goodsDescription || '',
      services: parseIdArray(h.service_ids).map(id => servicesList.find(s => s.id == id)).filter(Boolean),
      packingItems: parseIdArray(h.packing_item_ids).map(id => packingItems.find(p => p.id == id)).filter(Boolean),
      buildUpItems: parseIdArray(h.build_up_item_ids).map(id => buildUpItems.find(b => b.id == id)).filter(Boolean)
    }));

    // تحميل البوليصة كماستر جديد
    const loadedMaster = {
      tempId: `master_${Date.now()}`,
      id: bol.id,
      bolReference: bol.bol_reference || '',
      bolTypeCode: bol.bol_type_code || (normalizedHomes.length > 0 ? '741' : '740'),
      customerName: bol.customer_name || '',
      numberOfPackages: bol.number_of_packages || '',
      grossMass: bol.gross_mass || '',
      shippingMarks: bol.shipping_marks || '',
      goodsDescription: bol.goods_description || '',
      cargoTypes: parseIdArray(bol.cargo_types),
      services: parseIdArray(bol.service_ids).map(id => servicesList.find(s => s.id == id)).filter(Boolean),
      packingItems: parseIdArray(bol.packing_item_ids).map(id => packingItems.find(p => p.id == id)).filter(Boolean),
      buildUpItems: parseIdArray(bol.build_up_item_ids).map(id => buildUpItems.find(b => b.id == id)).filter(Boolean),
      homes: normalizedHomes
    };

    // تحميل الهيدر
    setHeader({
      ownerOperator: bol.owner_operator || '',
      aircraftRegistration: bol.aircraft_registration || '',
      declaredTotalAwbs: bol.declared_total_awbs || '',
      customsOfficeCode: bol.customs_office_code || '',
      voyageFlightNo: bol.voyage_flight_no || '',
      dateOfDeparture: bol.date_of_departure || '',
      etd: bol.etd || '',
      portOfLoading: bol.port_of_loading || '',
      portOfDischarge: bol.port_of_discharge || ''
    });

    setCurrentMaster(loadedMaster);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ─── Render ───
  return (
    <div className="page">
      <h2>Operator Application Form - AWB Submission</h2>

      {isSubmitted && (
        <div style={{ background: '#c8e6c9', color: '#1b5e20', padding: '15px', borderRadius: '4px', marginBottom: '20px', border: '1px solid #81c784' }}>
          ✓ All applications submitted successfully!
        </div>
      )}

      {/* ═══════════════════════════════════════
          الهيدر الثابت (مشترك لكل الماسترات)
         ═══════════════════════════════════════ */}
      <div className="card" style={{ border: '2px solid #4caf50', background: '#f1f8e9' }}>
        <h3>Flight/Voyage Information (Shared for all AWBs)</h3>
        <div className="grid grid-auto">
          <div className="field">
            <label>Owner or Operator *</label>
            <input type="text" value={header.ownerOperator} onChange={e => handleHeaderChange('ownerOperator', e.target.value)} placeholder="Airline or Freight Forwarder Name" />
          </div>
          <div className="field">
            <label>Aircraft Registration *</label>
            <input type="text" value={header.aircraftRegistration} onChange={e => handleHeaderChange('aircraftRegistration', e.target.value)} placeholder="e.g., N123AA" />
          </div>
          <div className="field">
            <label>Declared Total AWBs</label>
            <input type="number" value={header.declaredTotalAwbs} onChange={e => handleHeaderChange('declaredTotalAwbs', e.target.value)} placeholder="Total number of AWBs" />
          </div>
          <div className="field">
            <label>Flight No.</label>
            <input type="text" value={header.voyageFlightNo} onChange={e => handleHeaderChange('voyageFlightNo', e.target.value)} placeholder="Flight number or Voyage" />
          </div>
          <div className="field">
            <label>Date of Departure</label>
            <input type="date" value={header.dateOfDeparture} onChange={e => handleHeaderChange('dateOfDeparture', e.target.value)} />
          </div>
          <div className="field">
            <label>ETD</label>
            <input type="time" value={header.etd} onChange={e => handleHeaderChange('etd', e.target.value)} />
          </div>
          <div className="field">
            <label>Point of Loading (Origin) *</label>
            <input type="text" value={header.portOfLoading} onChange={e => handleHeaderChange('portOfLoading', e.target.value)} placeholder="Loading port/airport" />
          </div>
          <div className="field">
            <label>Point of Discharge (Destination) *</label>
            <input type="text" value={header.portOfDischarge} onChange={e => handleHeaderChange('portOfDischarge', e.target.value)} placeholder="Discharge port/airport" />
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          الماسترات المحفوظة
         ═══════════════════════════════════════ */}
      {savedMasters.length > 0 && (
        <div className="card">
          <h3>Saved Master AWBs ({savedMasters.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {savedMasters.map((m, idx) => (
              <div key={m.tempId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff' }}>
                <div>
                  <strong style={{ color: '#0d47a1' }}>{m.bolReference}</strong>
                  <span style={{ marginLeft: 10, color: '#666' }}>
                    — {m.numberOfPackages} pkg / {m.grossMass} kg — Houses: {m.homes?.length || 0}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => editSavedMaster(m)} type="button">Edit</button>
                  <button className="btn btn-danger" onClick={() => removeSavedMaster(m.tempId)} type="button">Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════
          زر إضافة ماستر جديد
         ═══════════════════════════════════════ */}
      {!currentMaster && (
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <button className="btn btn-primary" onClick={addNewMaster} type="button" style={{ fontSize: '16px', padding: '12px 30px' }}>
            + Add New Master AWB
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════
          فورم الماستر الحالي (قيد الإدخال)
         ═══════════════════════════════════════ */}
      {currentMaster && (
        <div className="card" style={{ border: '2px solid #2196f3', background: '#f0f7ff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ color: '#0d47a1', margin: 0 }}>Master AWB Entry</h3>
            <button className="btn btn-danger" onClick={cancelCurrentMaster} type="button">Cancel</button>
          </div>

          {/* Master Info */}
          <div className="grid grid-auto">
            <div className="field">
              <label>AWB Reference *</label>
              <input type="text" value={currentMaster.bolReference} onChange={e => updateCurrentMaster('bolReference', e.target.value)} />
            </div>
            <div className="field">
              <label>Number of Packages *</label>
              <input type="number" value={currentMaster.numberOfPackages} onChange={e => updateCurrentMaster('numberOfPackages', parseInt(e.target.value) || 0)} style={currentMaster.homes.length > 0 ? { background: '#e9ecef' } : {}} />
            </div>
            <div className="field">
              <label>Gross Mass (kg) *</label>
              <input type="number" value={currentMaster.grossMass} onChange={e => updateCurrentMaster('grossMass', parseFloat(e.target.value) || 0)} style={currentMaster.homes.length > 0 ? { background: '#e9ecef' } : {}} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Goods Description</label>
              <input type="text" value={currentMaster.goodsDescription} onChange={e => updateCurrentMaster('goodsDescription', e.target.value)} />
            </div>

            {/* Cargo Types */}
            <div className="field">
              <label>Cargo Types *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                {currentMaster.cargoTypes.map((type, idx) => (
                  <span key={idx} style={{ background: '#c8dbb8', borderRadius: 16, padding: '2px 8px', display: 'inline-flex', alignItems: 'center' }}>
                    {type}
                    <button onClick={() => updateCurrentMaster('cargoTypes', currentMaster.cargoTypes.filter(t => t !== type))} style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}>×</button>
                  </span>
                ))}
              </div>
              <div className="select-field">
                <select value="" onChange={e => {
                  const val = e.target.value;
                  if (val && !currentMaster.cargoTypes.includes(val)) updateCurrentMaster('cargoTypes', [...currentMaster.cargoTypes, val]);
                }}>
                  <option value="">Select Cargo Type...</option>
                  {cargoTypesList.filter(ct => !currentMaster.cargoTypes.includes(ct.name)).map(ct => (
                    <option key={ct.id} value={ct.name}>{ct.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Services */}
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Services Selection *</label>
              <div className="selected-items-container" style={{ flexDirection: 'column', gap: '10px' }}>
                {currentMaster.services.map(srvc => (
                  <div key={srvc.id} style={{ display: 'flex', flexDirection: 'column', gap: '5px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><strong>{srvc.type}:</strong> {srvc.classification}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', color: '#2e7d32', fontWeight: 'bold' }}>
                          <input type="checkbox" checked={srvc.showPacking || false} onChange={(e) => {
                            const checked = e.target.checked;
                            const newServices = currentMaster.services.map(s => s.id === srvc.id ? { ...s, showPacking: checked } : s);
                            const newPacking = (!checked && srvc.selectedPackingId) ? currentMaster.packingItems.filter(p => p.id !== srvc.selectedPackingId) : currentMaster.packingItems;
                            setCurrentMaster(prev => ({ ...prev, services: newServices, packingItems: newPacking }));
                          }} /> Add Packing
                        </label>
                        <button type="button" className="remove-btn" onClick={() => {
                          const newServices = currentMaster.services.filter(s => s.id !== srvc.id);
                          const newPacking = srvc.selectedPackingId ? currentMaster.packingItems.filter(p => p.id !== srvc.selectedPackingId) : currentMaster.packingItems;
                          setCurrentMaster(prev => ({ ...prev, services: newServices, packingItems: newPacking }));
                        }}>✕</button>
                      </div>
                    </div>
                    {srvc.showPacking && (
                      <div className="select-field" style={{ marginTop: '5px' }}>
                        {srvc.selectedPackingId ? (
                          <div className="item-chip" style={{ background: '#e2e8f0', justifyContent: 'space-between', display: 'flex', padding: '5px 10px' }}>
                            <span><strong>Packing:</strong> {(() => {
                              const p = packingItems.find(item => item.id == srvc.selectedPackingId);
                              return p ? `${p.type}${p.classification ? ` - ${p.classification}` : ''}` : '';
                            })()}</span>
                            <button type="button" className="remove-btn" onClick={() => {
                              setCurrentMaster(prev => ({
                                ...prev,
                                services: prev.services.map(s => s.id === srvc.id ? { ...s, selectedPackingId: null } : s),
                                packingItems: prev.packingItems.filter(p => p.id !== srvc.selectedPackingId)
                              }));
                            }}>✕</button>
                          </div>
                        ) : (
                          <select value="" onChange={e => {
                            const item = packingItems.find(p => p.id == e.target.value);
                            if (item) {
                              setCurrentMaster(prev => ({
                                ...prev,
                                services: prev.services.map(s => s.id === srvc.id ? { ...s, selectedPackingId: item.id } : s),
                                packingItems: !prev.packingItems.some(p => p.id == item.id) ? [...prev.packingItems, item] : prev.packingItems
                              }));
                            }
                          }}>
                            <option value="">Select Packing Type...</option>
                            {packingItems.filter(p => !currentMaster.packingItems.some(selected => selected.id === p.id)).map(p => (
                              <option key={p.id} value={p.id}>{p.type} - {p.classification}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="select-field" style={{ marginTop: '10px' }}>
                <select value="" onChange={e => handleAutoServiceSelect(e.target.value)}>
                  <option value="">+ Add Service Type...</option>
                  {uniqueServiceTypes.filter(t => !currentMaster.services.some(s => s.type === t)).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Master Packing Summary */}
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Master Packing Summary</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, minHeight: '35px', padding: '5px', background: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                {currentMaster.packingItems.length === 0 && <span className="hint-text">No packing selected</span>}
                {currentMaster.packingItems.map(item => (
                  <span key={item.id} style={{ background: '#c8dbb8', borderRadius: 16, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', fontSize: '11px' }}>
                    {item.type}{item.classification ? ` - ${item.classification}` : ''}
                    <button onClick={() => setCurrentMaster(prev => ({ ...prev, packingItems: prev.packingItems.filter(p => p.id !== item.id) }))} style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}>×</button>
                  </span>
                ))}
              </div>
            </div>

            {/* Build-up */}
            <div className="field">
              <label>Build-up Selection *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                {currentMaster.buildUpItems.map(item => (
                  <span key={item.id} style={{ background: '#c8dbb8', borderRadius: 16, padding: '2px 8px', display: 'inline-flex', alignItems: 'center' }}>
                    {item.name}
                    <button onClick={() => setCurrentMaster(prev => ({ ...prev, buildUpItems: prev.buildUpItems.filter(b => b.id !== item.id) }))} style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}>×</button>
                  </span>
                ))}
              </div>
              <div className="select-field">
                <select value="" onChange={e => {
                  const selectedId = e.target.value;
                  const fullObject = buildUpItems.find(b => b.id == selectedId);
                  if (fullObject && !currentMaster.buildUpItems.some(b => b.id == selectedId)) {
                    setCurrentMaster(prev => ({ ...prev, buildUpItems: [...prev.buildUpItems, fullObject] }));
                  }
                }}>
                  <option value="">Select Build-up Item...</option>
                  {buildUpItems.filter(b => !currentMaster.buildUpItems.some(selected => selected.id === b.id)).map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════
              House AWBs داخل هذا الماستر
             ═══════════════════════════════════════ */}
          <div style={{ marginTop: '20px' }}>
            <div className="card-header-row">
              <h4 style={{ color: '#1b5e20' }}>House AWBs inside this Master ({currentMaster.homes.length})</h4>
              <button type="button" className="btn btn-secondary" onClick={addHouseToCurrent}>+ Add House AWB</button>
            </div>
            <div className="selected-items-container" style={{ flexDirection: 'column', background: '#fff' }}>
              {currentMaster.homes.map((house, idx) => (
                <div key={house.tempId} className="card" style={{ marginBottom: '15px', border: '1px dashed #2196f3', padding: '20px', background: '#f0f7ff', borderRadius: '8px' }}>
                  <div className="card-header-row">
                    <strong style={{ color: '#0d47a1' }}>HOUSE AWB #{idx + 1}</strong>
                    <button type="button" className="btn btn-danger" onClick={() => removeHouseFromCurrent(house.tempId)}>Remove</button>
                  </div>
                  <div className="grid grid-auto small-gap">
                    <div className="field">
                      <label>House Ref *</label>
                      <input type="text" value={house.bolReference} onChange={e => updateHouseField(idx, 'bolReference', e.target.value)} />
                    </div>
                    <div className="field">
                      <label>Weight (kg) *</label>
                      <input type="number" value={house.grossMass} onChange={e => updateHouseField(idx, 'grossMass', e.target.value)} />
                    </div>
                    <div className="field">
                      <label>Packages *</label>
                      <input type="number" value={house.numberOfPackages} onChange={e => updateHouseField(idx, 'numberOfPackages', e.target.value)} />
                    </div>
                    <div className="field" style={{ gridColumn: 'span 2' }}>
                      <label>Goods Description</label>
                      <input type="text" value={house.goodsDescription} onChange={e => updateHouseField(idx, 'goodsDescription', e.target.value)} />
                    </div>
                  </div>

                  {/* House Build-up */}
                  <div className="grid grid-auto small-gap" style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
                    <div className="field">
                      <label>House Build-up</label>
                      <div className="selected-items-container">
                        {house.buildUpItems?.map(b => (
                          <div key={b.id} className="item-chip" style={{ background: '#fff' }}>
                            <small>{b.name}</small>
                            <button type="button" className="remove-btn" onClick={() => {
                              const newHomes = [...currentMaster.homes];
                              newHomes[idx].buildUpItems = house.buildUpItems.filter(item => item.id !== b.id);
                              setCurrentMaster(prev => ({ ...prev, homes: newHomes }));
                            }}>✕</button>
                          </div>
                        ))}
                      </div>
                      <div className="select-field">
                        <select value="" onChange={e => {
                          const item = buildUpItems.find(b => b.id == e.target.value);
                          if (item) {
                            const newHomes = [...currentMaster.homes];
                            newHomes[idx].buildUpItems = [...(house.buildUpItems || []), item];
                            setCurrentMaster(prev => ({ ...prev, homes: newHomes }));
                          }
                        }}>
                          <option value="">+ Add Build-up...</option>
                          {buildUpItems.filter(b => !house.buildUpItems?.some(hi => hi.id === b.id)).map(b => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* House Services & Packing */}
                  <div className="grid grid-auto small-gap" style={{ marginTop: '10px', gridColumn: '1 / -1' }}>
                    <div className="field">
                      <label>House Services & Packing</label>
                      <div className="selected-items-container" style={{ flexDirection: 'column', gap: '8px' }}>
                        {house.services.map(srv => (
                          <div key={srv.id} style={{ background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #bae6fd', width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <small><strong>{srv.type}:</strong> {srv.classification}</small>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', color: '#0288d1', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={srv.showPacking || false} onChange={(e) => {
                                    const checked = e.target.checked;
                                    const newHomes = [...currentMaster.homes];
                                    newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, showPacking: checked } : s);
                                    if (!checked && srv.selectedPackingId) {
                                      newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                    }
                                    setCurrentMaster(prev => ({ ...prev, homes: newHomes }));
                                  }} /> Add Packing
                                </label>
                                <button type="button" className="remove-btn" onClick={() => {
                                  const newHomes = [...currentMaster.homes];
                                  newHomes[idx].services = house.services.filter(s => s.id !== srv.id);
                                  if (srv.selectedPackingId) {
                                    newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                  }
                                  setCurrentMaster(prev => ({ ...prev, homes: newHomes }));
                                }}>✕</button>
                              </div>
                            </div>
                            {srv.showPacking && (
                              <div className="select-field" style={{ marginTop: '5px' }}>
                                {srv.selectedPackingId ? (
                                  <div className="item-chip" style={{ background: '#f1f5f9', justifyContent: 'space-between', display: 'flex', padding: '4px 8px' }}>
                                    <small><strong>Packing:</strong> {(() => {
                                      const p = packingItems.find(item => item.id == srv.selectedPackingId);
                                      return p ? `${p.type}${p.classification ? ` - ${p.classification}` : ''}` : '';
                                    })()}</small>
                                    <button type="button" className="remove-btn" onClick={() => {
                                      const newHomes = [...currentMaster.homes];
                                      newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, selectedPackingId: null } : s);
                                      newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                      setCurrentMaster(prev => ({ ...prev, homes: newHomes }));
                                    }}>✕</button>
                                  </div>
                                ) : (
                                  <select value="" onChange={e => {
                                    const item = packingItems.find(p => p.id == e.target.value);
                                    if (item) {
                                      const newHomes = [...currentMaster.homes];
                                      newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, selectedPackingId: item.id } : s);
                                      if (!house.packingItems.some(p => p.id == item.id)) {
                                        newHomes[idx].packingItems = [...house.packingItems, item];
                                      }
                                      setCurrentMaster(prev => ({ ...prev, homes: newHomes }));
                                    }
                                  }}>
                                    <option value="">Select House Packing...</option>
                                    {packingItems.filter(p => !house.packingItems.some(hi => hi.id === p.id)).map(p => (
                                      <option key={p.id} value={p.id}>{p.type} - {p.classification}</option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="select-field" style={{ marginTop: '8px' }}>
                        <select value="" onChange={e => handleHouseAutoServiceSelect(idx, e.target.value)}>
                          <option value="">+ Add Service Type to House...</option>
                          {uniqueServiceTypes.filter(t => !house.services.some(s => s.type === t)).map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="field">
                      <label>House Packing Summary</label>
                      <div className="selected-items-container">
                        {house.packingItems.map(p => (
                          <div key={p.id} className="item-chip" style={{ background: '#fff' }}>
                            <small>{p.type}{p.classification ? ` - ${p.classification}` : ''}</small>
                            <button type="button" className="remove-btn" onClick={() => {
                              const newHomes = [...currentMaster.homes];
                              newHomes[idx].packingItems = house.packingItems.filter(item => item.id !== p.id);
                              setCurrentMaster(prev => ({ ...prev, homes: newHomes }));
                            }}>✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Save Master Button */}
          <div className="actions-row" style={{ marginTop: '20px' }}>
            <button className="btn btn-primary" onClick={saveCurrentMaster} type="button">
              Save This Master AWB
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════
          زر Submit All
         ═══════════════════════════════════════ */}
      {(savedMasters.length > 0 || currentMaster) && (
        <div className="card" style={{ textAlign: 'center' }}>
          <button className="btn btn-success" onClick={submitAllMasters} type="button" style={{ fontSize: '16px', padding: '12px 40px' }}>
            Submit All Masters ({savedMasters.length + (currentMaster ? 1 : 0)})
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════
          Returned BOLs
         ═══════════════════════════════════════ */}
      {submittedBols.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3>Returned for Review</h3>
            <button className="btn btn-secondary" onClick={loadSubmittedBols} style={{ height: '38px' }}>Refresh</button>
          </div>
          <table className="grid-table">
            <thead>
              <tr>
                <th>AWB Reference</th>
                <th>Operator</th>
                <th>Status</th>
                <th>Houses</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {submittedBols.filter(b => !b.parent_bol_id).map(bol => (
                <tr key={bol.id}>
                  <td>{bol.bol_reference}</td>
                  <td>{bol.owner_operator}</td>
                  <td>
                    <span className={`badge ${bol.status === 'operator_review' ? 'badge-val' : 'badge-mail'}`}>
                      {bol.status === 'operator_review' ? '⚠️ Revision Required' : '⏳ Awaiting Services'}
                    </span>
                  </td>
                  <td>{(bol.homes || []).length}</td>
                  <td>
                    {(bol.status === 'operator_review' || bol.returned_for_review == 1) && (
                      <button className="btn btn-warning" onClick={() => editReturnedBol(bol)}>Edit & Resubmit</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OperatorApplicationForm;